import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileCsv,
  faTimes,
  faSpinner,
  faHourglassHalf,
  faCheckCircle,
  faTimesCircle,
  faHistory,
  faInfoCircle,
  faExclamationTriangle,
  faDownload,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import { getDprValidationUploadHistory, uploadDprValidationCsv } from "../../services/dprValidationUploadApi";
import { BASE_URL } from "../../config";
import BatchFileSpecsModal from "../Member/BatchFileSpecsModal";

const CSV_COLUMNS = [
  { name: "Trans No.",      required: true,  description: "Transaction number" },
  { name: "LOA",            required: true,  description: "Letter of Authorization code" },
  { name: "validate_status",required: true,  description: "Validation status label (e.g. Validated, Rejected)" },
  { name: "remarks",        required: false, description: "Optional remarks or notes" },
  { name: "Physician",      required: false, description: "Physician's name" },
  { name: "Transaction Amt.",required: true, description: "Transaction amount (numeric)" },
  { name: "Payment Ref. No.",required: false, description: "Optional payment reference number" },
];

const TEMPLATE_HEADERS = ["Trans No.", "LOA", "validate_status", "remarks", "Physician", "Transaction Amt.", "Payment Ref. No."];

const downloadCsvTemplate = () => {
  const csvContent = TEMPLATE_HEADERS.join(",") + "\n";
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "dpr_validation_template.csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};


const DprUploadValidationCsvPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [polledUploads, setPolledUploads] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [showSpecsModal, setShowSpecsModal] = useState(false);

  const allowedExts = [".csv", ".txt"];
  const maxSizeMB = 10;
  const maxFiles = 5;

  const validateFile = (file) => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExts.includes(ext)) {
      return "Only .csv or .txt files are accepted.";
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File size must not exceed ${maxSizeMB} MB.`;
    }
    return null;
  };

  const mergeFiles = (incomingFiles) => {
    const existing = [...selectedFiles];
    const next = [...existing];

    for (const file of incomingFiles) {
      if (next.length >= maxFiles) break;
      const duplicate = next.some((f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified);
      if (duplicate) continue;
      const err = validateFile(file);
      if (err) {
        setFileError(err);
        continue;
      }
      next.push(file);
    }

    if (incomingFiles.length > 0 && next.length === existing.length) {
      setFileError(`No files added. Check file type/size or maximum of ${maxFiles} files.`);
    } else if (next.length >= maxFiles && incomingFiles.length > 0) {
      setFileError(`Maximum of ${maxFiles} files reached.`);
    } else {
      setFileError(null);
    }

    setSelectedFiles(next);
    setResult(null);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    mergeFiles(files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    mergeFiles(files);
  };

  const handleRemoveFile = (index) => {
    const next = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(next);
    setFileError(null);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    const confirm = await Swal.fire({
      title: "Upload Validation CSV Files",
      text: `Upload ${selectedFiles.length} file(s) and start processing?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, upload",
      confirmButtonColor: "#0ea5e9",
      cancelButtonColor: "#6b7280",
    });
    if (!confirm.isConfirmed) return;

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files[]", file);
    });

    setUploading(true);
    setUploadProgress(0);
    setResult(null);

    try {
      const response = await uploadDprValidationCsv(formData, (progressEvent) => {
        const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(pct);
      });

      const data = response.data;
      const uploads = Array.isArray(data.uploads) ? data.uploads : [];
      setResult({
        success: true,
        uploads,
        uploaded_count: data.uploaded_count || 0,
        uploaded_by: data.uploaded_by || "",
        validated_by: data.validated_by || "",
        message: data.message,
      });
      const initialMap = {};
      uploads.forEach((upload) => {
        initialMap[upload.upload_id] = {
          status: upload.processing_status || "uploaded",
          total_rows: 0,
          success_count: 0,
          failed_count: 0,
          updated_at: null,
        };
      });
      setPolledUploads(initialMap);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Upload failed. Please try again.";
      setResult({ success: false, message: msg });
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const uploadIds = Array.isArray(result?.uploads)
      ? result.uploads.map((item) => item.upload_id).filter(Boolean)
      : [];

    if (!result?.success || uploadIds.length === 0) {
      setIsPolling(false);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const response = await getDprValidationUploadHistory({
          upload_ids: uploadIds.join(","),
          per_page: uploadIds.length,
          page: 1,
          sort_by: "created_at",
          sort_direction: "desc",
        });

        if (cancelled) return;

        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextMap = {};
        rows.forEach((row) => {
          nextMap[row.upload_id] = {
            status: row.status || "uploaded",
            total_rows: Number(row.total_rows || 0),
            success_count: Number(row.success_count || 0),
            failed_count: Number(row.failed_count || 0),
            updated_at: row.updated_at || null,
          };
        });
        setPolledUploads((prev) => {
          const merged = { ...prev, ...nextMap };
          const hasPending = uploadIds.some((uploadId) => {
            const status = merged[uploadId]?.status || "uploaded";
            return status !== "completed" && status !== "failed";
          });
          setIsPolling(hasPending);
          return merged;
        });
      } catch {
        if (!cancelled) {
          setIsPolling(false);
        }
      }
    };

    poll();
    const timer = setInterval(poll, 5000);

    return () => {
      cancelled = true;
      clearInterval(timer);
      setIsPolling(false);
    };
  }, [result]);

  const getStatusMeta = (status) => {
    if (status === "completed") {
      return { label: "completed", icon: faCheckCircle, className: "bg-emerald-100 text-emerald-700" };
    }
    if (status === "failed") {
      return { label: "failed", icon: faTimesCircle, className: "bg-red-100 text-red-700" };
    }
    if (status === "processing") {
      return { label: "processing", icon: faSpinner, className: "bg-amber-100 text-amber-700", spin: true };
    }
    return { label: "uploaded", icon: faHourglassHalf, className: "bg-blue-100 text-blue-700" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Header */}
        <div className="rounded-2xl bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-600 p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold tracking-tight">
            (DPR) Upload Validation CSV
          </h1>
          <p className="mt-1 text-sm text-sky-100">
            Upload a CSV file to validate DPR transactions. Processing is handled automatically via background queue.
          </p>
          <p className="mt-1 text-xs text-sky-100/90">
            "Validated By" is auto-set to the currently logged-in user who uploads the file(s).
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowSpecsModal(true)}
              className="relative inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/30 transition"
            >
              <span className="absolute -right-2 -top-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow">
                New
              </span>
              <FontAwesomeIcon icon={faBookOpen} />
              View File Specs
            </button>
            <button
              onClick={() => navigate("/dpr-validation-upload-history")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faHistory} />
              View Upload History
            </button>
          </div>
        </div>

        {/* Two-column layout: Left = CSV format info, Right = Upload form */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* LEFT: CSV Column Format Info */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-sky-700 font-semibold text-sm">
                <FontAwesomeIcon icon={faInfoCircle} />
                CSV Column Format (header row required)
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-sky-50 text-left text-xs uppercase text-sky-600">
                      <th className="px-3 py-2">Column</th>
                      <th className="px-3 py-2">Required</th>
                      <th className="px-3 py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {CSV_COLUMNS.map((col) => (
                      <tr key={col.name} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-gray-800 text-xs">{col.name}</td>
                        <td className="px-3 py-2">
                          {col.required ? (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Required</span>
                          ) : (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">Optional</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{col.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-400">
                Maximum 5,000 data rows per file. Columns must appear in the order shown above.
              </p>
              <div className="mt-4">
                <button
                  onClick={downloadCsvTemplate}
                  className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 transition"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download CSV Template
                </button>
              </div>
            </div>

            {/* Processing results (polled uploads) */}
            {result?.success && Array.isArray(result.uploads) && result.uploads.length > 0 && (
              <div className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">{result.message}</p>
                  {isPolling && (
                    <span className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Processingâ€¦
                    </span>
                  )}
                </div>
                {result.uploads.map((upload) => {
                  const polled = polledUploads[upload.upload_id] || {};
                  const status = polled.status || upload.processing_status || "uploaded";
                  const meta = getStatusMeta(status);
                  return (
                    <div key={upload.upload_id} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className="font-semibold text-gray-800 text-sm truncate max-w-[200px]">{upload.original_file_name}</p>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.className}`}>
                          <FontAwesomeIcon icon={meta.icon} spin={!!meta.spin} />
                          {meta.label}
                        </span>
                      </div>
                      {(status === "completed" || status === "failed") && polled.total_rows != null && (
                        <div className="mt-2 flex gap-4 text-xs">
                          <span className="text-gray-500">Total: <b className="text-gray-700">{polled.total_rows}</b></span>
                          <span className="text-emerald-600">Success: <b>{polled.success_count}</b></span>
                          <span className="text-red-500">Failed: <b>{polled.failed_count}</b></span>
                        </div>
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={() => navigate("/dpr-validation-upload-history")}
                  className="mt-1 text-xs text-sky-600 underline hover:text-sky-800"
                >
                  View full history â†’
                </button>
              </div>
            )}

            {result && !result.success && (
              <div className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                {typeof result.message === "string"
                  ? result.message
                  : JSON.stringify(result.message)}
              </div>
            )}
          </div>

          {/* RIGHT: Upload Form */}
          <div className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm space-y-5">
            <div className="text-sm font-semibold text-gray-700">CSV Files <span className="text-red-500">*</span></div>

            {/* Drag & Drop Zone */}
            <div>
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 transition
                  ${isDragging ? "border-sky-400 bg-sky-50" : "border-gray-200 bg-gray-50 hover:border-sky-300 hover:bg-sky-50"}
                `}
              >
                {selectedFiles.length ? (
                  <div className="w-full space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={`${file.name}-${file.lastModified}-${index}`} className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 w-full">
                        <FontAwesomeIcon icon={faFileCsv} className="text-2xl text-sky-500" />
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-semibold text-gray-800 text-sm">{file.name}</p>
                          <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRemoveFile(index); }}
                          className="ml-2 rounded-full p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">
                      {selectedFiles.length}/{maxFiles} file(s) selected.
                    </p>
                  </div>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} className="mb-3 text-3xl text-gray-300" />
                    <p className="text-sm font-semibold text-gray-500">Drag & drop up to {maxFiles} CSV files</p>
                    <p className="text-xs text-gray-400">or click to browse</p>
                    <p className="mt-2 text-xs text-gray-300">.csv or .txt &bull; max {maxSizeMB} MB each</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv,.txt,text/csv,text/plain"
                className="hidden"
                onChange={handleFileChange}
              />

              {fileError && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {fileError}
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div>
                <div className="mb-1 flex justify-between text-xs text-gray-500">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-sky-500 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50 transition"
            >
              {uploading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} />
                  Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File(s)` : "Files"}
                </>
              )}
            </button>

            <p className="text-xs text-gray-400 text-center">
              Files are queued for background processing after upload.
            </p>
          </div>

        </div>
        <BatchFileSpecsModal
          endpoint={`${BASE_URL}/provider-payment/dpr/validation-upload/specs`}
          isOpen={showSpecsModal}
          onClose={() => setShowSpecsModal(false)}
          title="DPR Validation Upload File Specs"
        />
      </div>
    </div>
  );
};

export default DprUploadValidationCsvPage;
